import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import TopBar from './TopBar';
import FoodDetailModal from './FoodDetailModal';
import ResidenceSelector from './ResidenceSelector';
import { getAllFoodItems, getFoodImageUrl } from '../services/foodItemService';
import { getFoodImages } from '../services/foodImageService';
import { getPickupPointById } from '../services/pickupPointService';
import { getProfileByUserId } from '../services/profileService';
import { getAverageRatingByVendorId } from '../services/reviewService';
import { getAllResidences } from '../services/residenceService';
import { addToCart, getCartItemCount } from '../services/cartService';
import './BuyFoodPage.css';

// Image Gallery Component
const ImageGallery = ({ images, altText }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <div className="meal-image-placeholder">
        <span>🍽️</span>
      </div>
    );
  }

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (e, index) => {
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  return (
    <div className="image-gallery">
      <img
        src={images[currentImageIndex]}
        alt={`${altText} - Image ${currentImageIndex + 1}`}
        className="meal-image"
        onError={(e) => {
          console.error('Failed to load image:', images[currentImageIndex]);
          e.target.style.display = 'none';
          e.target.parentElement.innerHTML = '<div class="meal-image-placeholder"><span>🍽️</span></div>';
        }}
      />

      {images.length > 1 && (
        <>
          <button className="gallery-nav-btn prev-btn" onClick={prevImage}>
            ‹
          </button>
          <button className="gallery-nav-btn next-btn" onClick={nextImage}>
            ›
          </button>

          <div className="image-counter">
            {currentImageIndex + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
};

export default function BuyFoodPage() {
  const navigate = useNavigate();
  const { user, profile, refreshCart } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();

  // Cart state
  const [cartCount, setCartCount] = useState(0);
  const [addingToCart, setAddingToCart] = useState(null); // Track which item is being added

  // Mobile filter state
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Filter States
  const [filters, setFilters] = useState({
    mealTypes: {
      breakfast: false,
      lunch: false,
      dinner: false
    },
    priceRange: {
      min: '',
      max: ''
    },
    delivery: {
      vegetarian: false
    },
    availability: {
      availableNow: false,
      preOrder: false
    }
  });

  const [sortBy, setSortBy] = useState('relevance');
  const [selectedLocation, setSelectedLocation] = useState('Monash Waterfront Residence');
  const [selectedResidenceId, setSelectedResidenceId] = useState(null);
  const [residences, setResidences] = useState([]);

  // Data states
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle residence selection
  const handleResidenceSelect = (residenceName) => {
    setSelectedLocation(residenceName);
    const residence = residences.find(r => r.name === residenceName);
    if (residence) {
      setSelectedResidenceId(residence.id);
      console.log('Selected residence:', residenceName, 'ID:', residence.id);
    }
  };

  // Modal state
  const [selectedFood, setSelectedFood] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch residences on mount
  useEffect(() => {
    const fetchResidences = async () => {
      try {
        const condos = await getAllResidences();
        setResidences(condos);

        // Set default residence based on user profile or first in list
        if (profile?.condo_id) {
          const userCondo = condos.find(c => c.id === profile.condo_id);
          if (userCondo) {
            setSelectedLocation(userCondo.name);
            setSelectedResidenceId(userCondo.id);
          }
        } else if (condos.length > 0) {
          setSelectedLocation(condos[0].name);
          setSelectedResidenceId(condos[0].id);
        }
      } catch (error) {
        console.error('Error fetching residences:', error);
      }
    };

    fetchResidences();
  }, [profile]);

  // Fetch food items from database
  useEffect(() => {
    const fetchFoodItems = async () => {
      try {
        setLoading(true);
        setError(null);

        const foodItems = await getAllFoodItems();
        console.log('Fetched food items:', foodItems);

        // Transform database food items to display format
        const transformedMeals = await Promise.all(foodItems.map(async (item) => {
          // Get all images for this food item
          console.log('Processing item:', item.item_name, 'ID:', item.$id);
          let imageUrls = [];
          try {
            const foodImages = await getFoodImages(item.$id);
            console.log('Found', foodImages.length, 'images for', item.item_name);
            imageUrls = foodImages.map(img => getFoodImageUrl(img.imageId));
            console.log('Generated image URLs:', imageUrls);
          } catch (imageError) {
            console.error('Error getting images for food item:', imageError);
          }

          // Use first image as primary image for display
          const primaryImage = imageUrls.length > 0 ? imageUrls[0] : '';

          // Get pickup point details
          let pickupLocation = 'Pickup location';
          let condoId = null;
          try {
            if (item.pickup_id) {
              const pickupPoint = await getPickupPointById(item.pickup_id);
              pickupLocation = pickupPoint.name;
              condoId = pickupPoint.condoId; // Store the condo ID for filtering
            }
          } catch (pickupError) {
            console.error('Error getting pickup point:', pickupError);
          }

          // Get vendor details
          let vendorName = 'Vendor';
          try {
            if (item.vendor_id) {
              const vendorProfile = await getProfileByUserId(item.vendor_id);
              vendorName = vendorProfile.vendor_name || vendorProfile.full_name || 'Vendor';
            }
          } catch (vendorError) {
            console.error('Error getting vendor profile:', vendorError);
          }

          // Format meal type for display and filtering - handle both string and array formats
          let mealTypes = [];
          if (Array.isArray(item.meal_type)) {
            mealTypes = item.meal_type.map(t => t.toLowerCase());
          } else if (typeof item.meal_type === 'string') {
            mealTypes = item.meal_type.toLowerCase().split(',').map(t => t.trim());
          }

          // Display formatted version (Capitalized and joined)
          const displayMealType = mealTypes
            .map(t => t.charAt(0).toUpperCase() + t.slice(1))
            .join(', ') || 'Lunch';
          const primaryMealType = mealTypes[0] || 'lunch';

          // Format cutoff time (date + time)
          const cutoffDate = new Date(item.cutoff_time);
          const orderByTime = cutoffDate && !isNaN(cutoffDate.getTime()) ? (
            cutoffDate.toLocaleDateString('en-US', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            }) + ', ' + cutoffDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })
          ) : 'Not specified';

          // Format pickup time (date + time)
          const pickupDate = new Date(item.pickup_time);
          const pickupTime = pickupDate && !isNaN(pickupDate.getTime()) ? (
            pickupDate.toLocaleDateString('en-US', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            }) + ', ' + pickupDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })
          ) : 'Not specified';

          // Get average rating for this vendor (may be null if no reviews)
          let avgRating = null;
          try {
            if (item.vendor_id) {
              const ar = await getAverageRatingByVendorId(item.vendor_id);
              avgRating = ar;
            }
          } catch (ratingErr) {
            console.error('Error fetching average rating for vendor:', ratingErr);
          }

          return {
            id: item.$id,
            name: item.item_name,
            price: item.price,
            image: primaryImage,
            images: imageUrls, // Store all image URLs
            mealType: displayMealType,
            mealTypes: mealTypes, // Store all meal types
            rating: avgRating, // null means no rating yet
            vendor: vendorName,
            vendorId: item.vendor_id,
            orderBy: orderByTime,
            pickup: pickupTime,
            pickupLocation: pickupLocation,
            pickupId: item.pickup_id,
            condoId: condoId, // Add condo ID for filtering
            ordersLeft: item.stock_count,
            availability: item.is_preorder ? 'preorder' : 'available',
            isVegetarian: Array.isArray(item.dietary_label)
              ? item.dietary_label.some(l => l.toLowerCase() === 'vegetarian')
              : (item.dietary_label?.toLowerCase().includes('vegetarian') || false),
            description: item.description,
            ingredients: item.ingredients ? item.ingredients.split(',').map(i => i.trim()) : [],
            dietaryLabel: item.dietary_label,
            cutoffTime: item.cutoff_time,
            pickupTime: item.pickup_time
          };
        }));

        setMeals(transformedMeals);
      } catch (err) {
        console.error('Error fetching food items:', err);
        setError('Failed to load food items. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFoodItems();
  }, []);

  // Filtered meals based on active filters
  const [filteredMeals, setFilteredMeals] = useState(meals);

  // Modal handlers
  const handleFoodClick = (meal) => {
    // Use the meal data which already has description and ingredients from database
    const foodDetail = {
      ...meal,
      category: meal.mealType,
      vendorName: meal.vendor,
      orderBy: meal.orderBy,
      pickupTime: meal.pickup,
      pickupLocation: meal.pickupLocation,
      description: meal.description || 'Delicious homemade meal prepared with care.',
      ingredients: meal.ingredients || [],
      notice: 'Food will be delivered to the pickup location as specified.',
      ordersLeft: meal.ordersLeft,
      dietaryLabel: meal.dietaryLabel
    };
    setSelectedFood(foodDetail);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFood(null);
  };

  // Fetch cart count on mount and when user changes
  useEffect(() => {
    const fetchCartCount = async () => {
      if (user) {
        try {
          const count = await getCartItemCount(user.$id);
          setCartCount(count);
        } catch (error) {
          console.error('Error fetching cart count:', error);
        }
      }
    };

    fetchCartCount();
  }, [user]);

  const handleAddToCart = async (cartItem) => {
    console.log('Adding to cart from BuyFoodPage:', cartItem);

    if (!user) {
      showInfo('Please log in to add items to cart');
      navigate('/login');
      return;
    }

    // Prevent vendors from adding their own items to cart
    if (cartItem && cartItem.vendorId && user && user.$id === cartItem.vendorId) {
      showError('You cannot add your own food items to the cart.');
      return;
    }

    try {
      setAddingToCart(cartItem.foodId);

      await addToCart(
        user.$id,
        cartItem.foodId,
        cartItem.quantity || 1
      );

      // Update cart count
      const newCount = await getCartItemCount(user.$id);
      setCartCount(newCount);

      // Refresh TopBar cart
      if (refreshCart) refreshCart();

      showSuccess(`${cartItem.foodName} added to cart! ✓`);
    } catch (error) {
      console.error('[Add to Cart Error]:', error);
      showError(`[Add to Cart Failure] ${error.message || 'Please try again.'}`);
    } finally {
      setAddingToCart(null);
    }
  };

  const handleQuickAddToCart = async (e, meal) => {
    e.stopPropagation(); // Prevent opening the modal

    if (!user) {
      showInfo('Please log in to add items to cart');
      navigate('/login');
      return;
    }

    // Prevent vendor from quick-adding their own item
    if (user && user.$id === meal.vendorId) {
      showError('You cannot add your own food items to the cart.');
      return;
    }

    try {
      setAddingToCart(meal.id);

      console.log('Quick adding to cart:', { userId: user.$id, mealId: meal.id });
      await addToCart(user.$id, meal.id, 1);

      // Update cart count
      const newCount = await getCartItemCount(user.$id);
      setCartCount(newCount);

      // Refresh TopBar cart
      if (refreshCart) refreshCart();

      // Show toast notification
      showSuccess(`${meal.name} added to cart! ✓`);

      // Show success feedback on button
      const button = e.currentTarget;
      if (button) {
        const originalText = button.innerHTML;
        button.innerHTML = '✓ Added';
        button.style.backgroundColor = '#28a745';

        setTimeout(() => {
          if (button) {
            button.innerHTML = originalText;
            button.style.backgroundColor = '';
          }
        }, 1500);
      }
    } catch (error) {
      console.error('[Quick Add Action Error]:', error);
      showError(`[Quick Add Failure] ${error.message || 'Please try again.'}`);
    } finally {
      setAddingToCart(null);
    }
  };

  // Apply filters whenever filters, meals, or selected residence change
  useEffect(() => {
    let result = [...meals];

    // Filter by selected residence first
    if (selectedResidenceId) {
      result = result.filter(meal => meal.condoId === selectedResidenceId);
    }

    // Filter by meal type
    const selectedMealTypes = Object.keys(filters.mealTypes).filter(
      key => filters.mealTypes[key]
    );
    if (selectedMealTypes.length > 0) {
      result = result.filter(meal => {
        // Check if any of the meal's types match the selected filters
        return meal.mealTypes?.some(type => selectedMealTypes.includes(type)) ||
          selectedMealTypes.includes(meal.mealType);
      });
    }

    // Filter by price range
    if (filters.priceRange.min !== '') {
      result = result.filter(meal => meal.price >= parseFloat(filters.priceRange.min));
    }
    if (filters.priceRange.max !== '') {
      result = result.filter(meal => meal.price <= parseFloat(filters.priceRange.max));
    }

    // Filter by vegetarian
    if (filters.delivery.vegetarian) {
      result = result.filter(meal => meal.isVegetarian);
    }

    // Filter by availability
    const selectedAvailability = [];
    if (filters.availability.availableNow) selectedAvailability.push('available');
    if (filters.availability.preOrder) selectedAvailability.push('preorder');
    if (selectedAvailability.length > 0) {
      result = result.filter(meal => selectedAvailability.includes(meal.availability));
    }

    // Sort meals
    // Sort meals: Prioritize available items (not sold out AND not expired)
    const nowSort = new Date();
    result.sort((a, b) => {
      const aSoldOut = a.ordersLeft === 0;
      const aExpired = a.cutoffTime && new Date(a.cutoffTime) < nowSort;
      const aAvailable = !aSoldOut && !aExpired;

      const bSoldOut = b.ordersLeft === 0;
      const bExpired = b.cutoffTime && new Date(b.cutoffTime) < nowSort;
      const bAvailable = !bSoldOut && !bExpired;

      // Available items always come first
      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;

      // If both have same availability status, use the selected sort criteria
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'rating':
          return (b.rating ?? -1) - (a.rating ?? -1);
        default:
          return 0; // Maintain original order (relevance)
      }
    });

    setFilteredMeals(result);
  }, [filters, meals, sortBy, selectedResidenceId]);

  // Handle filter changes
  const handleMealTypeChange = (type) => {
    setFilters(prev => ({
      ...prev,
      mealTypes: {
        ...prev.mealTypes,
        [type]: !prev.mealTypes[type]
      }
    }));
  };

  const handlePriceChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      priceRange: {
        ...prev.priceRange,
        [field]: value
      }
    }));
  };

  const handleVegetarianChange = () => {
    setFilters(prev => ({
      ...prev,
      delivery: {
        vegetarian: !prev.delivery.vegetarian
      }
    }));
  };

  const handleAvailabilityChange = (type) => {
    setFilters(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [type]: !prev.availability[type]
      }
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      mealTypes: {
        breakfast: false,
        lunch: false,
        dinner: false
      },
      priceRange: {
        min: '',
        max: ''
      },
      delivery: {
        vegetarian: false
      },
      availability: {
        availableNow: false,
        preOrder: false
      }
    });
    // Close mobile filters after clearing on mobile
    if (window.innerWidth <= 1024) {
      setShowMobileFilters(false);
    }
  };

  const applyFilters = () => {
    // Filters are applied automatically via useEffect
    // This function can be used for additional actions if needed
    console.log('Filters applied:', filters);
    // Close mobile filters after applying
    setShowMobileFilters(false);
  };

  const toggleMobileFilters = () => {
    setShowMobileFilters(!showMobileFilters);
  };

  // Prevent body scroll when mobile filters are open
  useEffect(() => {
    if (showMobileFilters) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileFilters]);

  return (
    <div className="buy-food-page">
      <TopBar />

      <div className="buy-food-container">
        {/* Mobile Filter Button */}
        <button className="mobile-filter-toggle-btn" onClick={toggleMobileFilters}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 4H21V6H3V4ZM7 11H17V13H7V11ZM11 18H13V20H11V18Z" fill="currentColor" />
          </svg>
          Filter by
        </button>

        {/* Overlay for mobile filters */}
        {showMobileFilters && (
          <div className="mobile-filter-overlay" onClick={toggleMobileFilters}></div>
        )}

        {/* Left Sidebar - Filters */}
        <div className={`filter-sidebar-wrapper ${showMobileFilters ? 'mobile-show' : ''}`}>
          <button className="mobile-filter-close-btn" onClick={toggleMobileFilters}>
            ✕
          </button>
          <div className="filter-header">
            <div className="filter-header-content">
              <h2 className="filter-title">Filter by</h2>
              <button className="filter-clear-all-btn" onClick={clearAllFilters}>
                Clear All
              </button>
            </div>
          </div>
          <aside className="buy-food-sidebar">

            {/* Meal Type Filter */}
            <div className="filter-section">
              <h3 className="filter-section-title">Meal Type</h3>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.mealTypes.breakfast}
                  onChange={() => handleMealTypeChange('breakfast')}
                />
                <span>Breakfast</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.mealTypes.lunch}
                  onChange={() => handleMealTypeChange('lunch')}
                />
                <span>Lunch</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.mealTypes.dinner}
                  onChange={() => handleMealTypeChange('dinner')}
                />
                <span>Dinner</span>
              </label>
            </div>

            {/* Price Filter */}
            <div className="filter-section">
              <h3 className="filter-section-title">Price</h3>
              <div className="price-inputs">
                <div className="price-input-group">
                  <input
                    type="number"
                    id="minPrice"
                    name="minPrice"
                    placeholder="RM"
                    value={filters.priceRange.min}
                    onChange={(e) => handlePriceChange('min', e.target.value)}
                  />
                  <label htmlFor="minPrice">MIN</label>
                </div>
                <div className="price-input-group">
                  <input
                    type="number"
                    id="maxPrice"
                    name="maxPrice"
                    placeholder="RM"
                    value={filters.priceRange.max}
                    onChange={(e) => handlePriceChange('max', e.target.value)}
                  />
                  <label htmlFor="maxPrice">MAX</label>
                </div>
              </div>
            </div>

            {/* Delivery Filter */}
            <div className="filter-section">
              <h3 className="filter-section-title">Dietary Restrictions</h3>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.delivery.vegetarian}
                  onChange={handleVegetarianChange}
                />
                <span>Vegetarian</span>
              </label>
            </div>

            {/* Availability Filter */}
            <div className="filter-section">
              <h3 className="filter-section-title">Availability</h3>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.availability.availableNow}
                  onChange={() => handleAvailabilityChange('availableNow')}
                />
                <span>Available now</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.availability.preOrder}
                  onChange={() => handleAvailabilityChange('preOrder')}
                />
                <span>Pre-order</span>
              </label>
            </div>

            {/* Apply Filters Button */}
            <button className="apply-filters-btn" onClick={applyFilters}>
              Apply Filters
            </button>
          </aside>
        </div>

        {/* Main Content Area */}
        <main className="buy-food-main">
          {/* Header Section with Residence Selector */}
          <div className="buy-food-header">
            <ResidenceSelector
              selectedResidence={selectedLocation}
              onResidenceSelect={handleResidenceSelect}
              label="Showing food available in"
              showIcon={true}
            />
            <div className="results-info">
              <span className="results-count">
                {filteredMeals.length} food items found
              </span>
            </div>
          </div>

          {/* Location and Sort Bar */}
          <div className="location-sort-bar">
            <div className="sort-container">
              <label htmlFor="sortBy">Sort by:</label>
              <select
                id="sortBy"
                name="sortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="relevance">Relevance</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Rating</option>
              </select>
            </div>
          </div>

          {/* Meals Grid */}
          <div className="meals-grid">
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading delicious food items...</p>
              </div>
            ) : error ? (
              <div className="error-container">
                <p className="error-message">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="retry-button"
                >
                  Retry
                </button>
              </div>
            ) : filteredMeals.length === 0 ? (
              <div className="no-results-container">
                <p className="no-results-message">
                  {meals.length === 0
                    ? 'No food items available at the moment. Check back later!'
                    : 'No food items match your filters. Try adjusting your search criteria.'}
                </p>
                {meals.length > 0 && (
                  <button onClick={clearAllFilters} className="clear-filters-button">
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              filteredMeals.map(meal => {
                const isSoldOut = meal.ordersLeft === 0;
                const isExpired = meal.cutoffTime && new Date(meal.cutoffTime) < new Date();
                const isUnavailable = isSoldOut || isExpired;

                return (
                  <div
                    key={meal.id}
                    className={`meal-card ${isUnavailable ? 'unavailable' : ''}`}
                    onClick={() => handleFoodClick(meal)}
                    style={{ cursor: isUnavailable ? 'default' : 'pointer' }}
                  >
                    <div className="meal-image-container">
                      <ImageGallery
                        images={meal.images && meal.images.length > 0 ? meal.images : []}
                        altText={meal.name}
                      />
                      <span className={`availability-badge ${meal.availability}`}>
                        {meal.availability === 'available' ? 'Available now' : 'Pre-order'}
                      </span>
                      {isExpired && isSoldOut && (
                        <div className="status-overlay unavailable">
                          Unavailable
                        </div>
                      )}
                      {isExpired && !isSoldOut && (
                        <div className="status-overlay expired">
                          Ordering Period Over
                        </div>
                      )}
                      {isSoldOut && !isExpired && (
                        <div className="status-overlay sold-out">
                          Sold Out
                        </div>
                      )}
                    </div>

                    <div className="meal-content">
                      <div className="meal-header">
                        <span className="meal-type">{meal.mealType.charAt(0).toUpperCase() + meal.mealType.slice(1)}</span>
                        <div className="meal-rating">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFA500" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" />
                          </svg>
                          <span>{meal.rating || meal.rating === 0 ? meal.rating.toFixed(1) : 'No Rating Yet'}</span>
                        </div>
                      </div>

                      <div className="meal-name-container">
                        <h3 className="meal-name">{meal.name}</h3>
                        {meal.isVegetarian && (
                          <span className="vegetarian-icon" title="Vegetarian">🌱</span>
                        )}
                      </div>

                      <p className="meal-price">RM {meal.price}</p>

                      {meal.isVegetarian && (
                        <div className="vegetarian-badge" title="Vegetarian">
                          <span className="veg-icon" aria-hidden="true">🟢</span>
                          <span className="veg-text" title="Vegetarian">Vegetarian</span>
                        </div>
                      )}

                      <div className="meal-details">
                        <p className="meal-vendor">Prepared by {meal.vendor}</p>
                        <p className="meal-order-time">Order by: {meal.orderBy}</p>
                        <p className="meal-pickup">Pickup: {meal.pickup}</p>
                      </div>

                      <p className="orders-left">Orders left: {meal.ordersLeft}</p>

                      <button
                        className="add-to-cart-btn"
                        onClick={(e) => handleQuickAddToCart(e, meal)}
                        disabled={addingToCart === meal.id || isUnavailable || (user && user.$id === meal.vendorId)}
                        title={user && user.$id === meal.vendorId ? 'You cannot add your own item to cart' : undefined}
                      >
                        {addingToCart === meal.id ? (
                          <>
                            <span className="spinner-small"></span> Adding...
                          </>
                        ) : (isSoldOut && isExpired) ? (
                          'Unavailable'
                        ) : isSoldOut ? (
                          'Sold Out'
                        ) : isExpired ? (
                          'Ordering Period Over'
                        ) : user && user.$id === meal.vendorId ? (
                          'Your Item'
                        ) : (
                          <>
                            <span>🛒</span> Add to Cart
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </main>
      </div>

      {/* Food Detail Modal */}
      <FoodDetailModal
        food={selectedFood}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAddToCart={handleAddToCart}
        isOwner={user && selectedFood && user.$id === selectedFood.vendorId}
        onUpdate={(updated) => {
          setSelectedFood(updated);
          setMeals(prev => prev.map(m => m.id === updated.id ? { ...m, ingredients: updated.ingredients } : m));
        }}
      />


    </div >
  );
}
